import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';
import {
  FileText, CheckCircle, Calendar, DollarSign, AlertCircle,
  RefreshCw, Star, User, Clock, ChevronDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const TYPE_CONFIG = {
  invoice:        { icon: FileText,    color: '#C9A84C', bg: 'bg-amber-50',   label: 'Invoice Sent' },
  invoice_paid:   { icon: CheckCircle, color: '#16a34a', bg: 'bg-green-50',   label: 'Invoice Paid' },
  payment:        { icon: DollarSign,  color: '#16a34a', bg: 'bg-green-50',   label: 'Payment' },
  booking:        { icon: Calendar,    color: '#68caf1', bg: 'bg-sky-50',     label: 'Booking' },
  one_time:       { icon: Calendar,    color: '#68caf1', bg: 'bg-sky-50',     label: 'Booking' },
  weekend:        { icon: Calendar,    color: '#68caf1', bg: 'bg-sky-50',     label: 'Booking' },
  maintenance:    { icon: Star,        color: '#C9A84C', bg: 'bg-amber-50',   label: 'Maintenance' },
  member:         { icon: Star,        color: '#C9A84C', bg: 'bg-amber-50',   label: 'Member' },
  status_change:  { icon: RefreshCw,   color: '#6b7280', bg: 'bg-gray-50',   label: 'Status' },
  reschedule:     { icon: RefreshCw,   color: '#6b7280', bg: 'bg-gray-50',   label: 'Reschedule' },
  client:         { icon: User,        color: '#8b5cf6', bg: 'bg-purple-50', label: 'Client' },
  error:          { icon: AlertCircle, color: '#dc2626', bg: 'bg-red-50',    label: 'Error' },
  default:        { icon: Clock,       color: '#6b7280', bg: 'bg-gray-50',   label: 'Activity' },
};

const FILTER_TABS = [
  { key: 'all',      label: 'All' },
  { key: 'booking',  label: 'Bookings',  types: ['booking', 'one_time', 'weekend'] },
  { key: 'payment',  label: 'Payments',  types: ['payment', 'invoice', 'invoice_paid'] },
  { key: 'member',   label: 'Members',   types: ['member', 'maintenance'] },
  { key: 'error',    label: 'Errors',    types: ['error'] },
];

const PAGE_SIZE = 30;

function getConfig(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.default;
}

function ActivityRow({ item }) {
  const { icon: Icon, color, bg } = getConfig(item.type);
  const timeAgo = item.created_at
    ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
    : '';

  return (
    <div className="flex items-start gap-3 px-4 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
        <Icon size={15} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-snug">{item.note}</p>
        <p className="text-xs text-gray-400 mt-0.5">{timeAgo}</p>
      </div>
    </div>
  );
}

export default function Activities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchItems = useCallback(async (reset = false) => {
    const start = reset ? 0 : offset;
    const tab = FILTER_TABS.find(t => t.key === filter);

    let q = supabase
      .from('activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .range(start, start + PAGE_SIZE - 1);

    if (tab?.types) {
      q = q.in('type', tab.types);
    }

    const { data } = await q;
    const rows = data || [];

    if (reset) {
      setItems(rows);
      setOffset(PAGE_SIZE);
    } else {
      setItems(prev => [...prev, ...rows]);
      setOffset(start + PAGE_SIZE);
    }
    setHasMore(rows.length === PAGE_SIZE);
  }, [filter, offset]);

  // Reset when filter changes
  useEffect(() => {
    setLoading(true);
    setOffset(0);
    setHasMore(true);

    const tab = FILTER_TABS.find(t => t.key === filter);
    let q = supabase
      .from('activity_log')
      .select('*')
      .order('created_at', { ascending: false })
      .range(0, PAGE_SIZE - 1);

    if (tab?.types) q = q.in('type', tab.types);

    q.then(({ data }) => {
      setItems(data || []);
      setOffset(PAGE_SIZE);
      setHasMore((data || []).length === PAGE_SIZE);
      setLoading(false);
    });
  }, [filter]);

  // Real-time new entries
  useEffect(() => {
    const channel = supabase
      .channel('activities-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'activity_log' }, (payload) => {
        const tab = FILTER_TABS.find(t => t.key === filter);
        const matches = !tab?.types || tab.types.includes(payload.new.type);
        if (matches) setItems(prev => [payload.new, ...prev]);
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [filter]);

  const loadMore = async () => {
    setLoadingMore(true);
    await fetchItems(false);
    setLoadingMore(false);
  };

  return (
    <div className="max-w-2xl mx-auto pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-[#F5F5F0] z-10 pt-4 pb-2 px-4">
        <h1 className="text-xl font-bold text-gray-900 mb-3">Activity</h1>

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                filter === tab.key
                  ? 'bg-[#C9A84C] text-black'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feed */}
      <div className="bg-white rounded-2xl overflow-hidden mx-4 mt-3 shadow-sm border border-gray-100">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading activity...</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No activity yet</div>
        ) : (
          <>
            {items.map((item, i) => <ActivityRow key={item.id || i} item={item} />)}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-3 text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <ChevronDown size={15} />
                {loadingMore ? 'Loading...' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
